import { api } from "../../api";

const createQueryParams = (params, isAdditionalFilters) => {
    if (!params || typeof params !== "object") return "";

    const appliedFilters = Object.keys(params)
        .map((key) => {
            const value = params[key];
            if (value === undefined || value === null || value === "")
                return "";
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(Boolean)
        .join("&");

    if (!appliedFilters) return "";
    return isAdditionalFilters ? `?${appliedFilters}` : `&${appliedFilters}`;
};

export const salesApi = api.injectEndpoints({
    endpoints: (build) => ({
        getLazyActiveTiers: build.query({
            query: ({ include_clients }) =>
                `/sales/active-tiers/?export_to_excel=true&include_clients=${include_clients}`,
        }),
        getLazyClientLocations: build.query({
            query: () => `/sales/client-geography/`,
        }),
        getLazyClientDetail: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params);
                return `/sales/client-details/?export_to_excel=true&${appliedFilters}`;
            },
        }),
        getLazyClientAccounts: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/sales/accounts-client/?export_to_excel=true&${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyBusinessTrends: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/sales/business-trend/?export_to_excel=true&${appliedFilters}`;
            },
        }),
        getLazyInteractionTrends: build.query({
            query: ({ id, start_date, end_date }) =>
                `/sales/interactions/?export_to_excel=true&client_id=${id}&start_date=${start_date}&end_date=${end_date}`,
        }),
        getLazyClientFinancials: build.query({
            query: (params) => {
                return `/sales/client-financials/?export_to_excel=true&date=${params.endDate}`;
            },
        }),
        getLazyClientVote: build.query({
            query: (params) => {
                return `/sales/client-vote/?export_to_excel=true&date=${params.endDate}&client_id=${params.appliedFilters.client_id}`
            }
        }),
        getLazyClientTouchPoints: build.query({
            query: (params) => {
                return `/sales/client-touchpoints/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}&client_id=${params.appliedFilters.client_id}`
            }
        }),
        getLazyClientTrends: build.query({
            query: (params) => {
                return `/sales/client-trends/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}&client_id=${params.appliedFilters.client_id}`
            }
        }),
        getLazyUserList: build.query({
            query: () => `/sales/user-list/`,
        }),
        getLazyTodoItems: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params);
                return `/sales/todo/?export_to_excel=true&${appliedFilters}`;
            },
            providesTags: ["Todo"],
        }),
        createTodoItem: build.mutation({
            query: ({ data }) => ({
                url: `/sales/todo/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Todo"],
        }),
        updateTodoItem: build.mutation({
            query: ({ data }) => ({
                url: `/sales/todo/${data.id}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Todo"],
        }),
        deleteTodoItem: build.mutation({
            query: ({ id }) => ({
                url: `/sales/todo/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["Todo"],
        }),
    }),
    overrideExisting: false,
});

export const {
    useLazyGetLazyActiveTiersQuery,
    useLazyGetLazyClientAccountsQuery,
    useLazyGetLazyClientLocationsQuery,
    useLazyGetLazyClientFinancialsQuery,
    useLazyGetLazyClientVoteQuery,
    useLazyGetLazyClientTouchPointsQuery,
    useLazyGetLazyClientTrendsQuery,
    useLazyGetLazyBusinessTrendsQuery,
    useLazyGetLazyInteractionTrendsQuery,
    useLazyGetLazyClientDetailQuery,
    useLazyGetLazyUserListQuery,
    useLazyGetLazyTodoItemsQuery,
    useCreateTodoItemMutation,
    useUpdateTodoItemMutation,
    useDeleteTodoItemMutation,
} = salesApi;
